import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiService from '../../../services/api';
import { useNotification } from '../../../contexts/NotificationContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useConfirm } from '../../../contexts/ConfirmContext';
import { ApiResponse } from '../../../types';
import EmptyState from '../../UI/EmptyState';
import SkeletonLoader from '../../UI/SkeletonLoader';
import SidePanel from '../../UI/SidePanel';
import CustomDropdown, { DropdownOption } from '../../UI/CustomDropdown';
import StatusBadge from './StatusBadge';
import styles from '../../../styles/TaskDetails.module.css';
import buttonStyles from '../../../styles/Buttons.module.css';
import sidePanelStyles from '../../../styles/SidePanel.module.css';
import customDropdownStyles from '../../../styles/CustomDropdown.module.css';

interface User {
  id: string;
  first_name: string;
  last_name: string;
  full_name: string;
  email: string;
}

interface TaskCommentAttachment {
  id: string;
  filename: string;
  file_size: number;
  file_type: string;
  file_size_display: string;
  url: string;
}

interface TaskCommentReaction {
  count: number;
  users: {
    first_name: string;
    last_name: string;
    username: string;
    full_name: string;
  }[];
}

interface SubmissionTimelineItem {
  id: string;
  type: string;
  title: string;
  description: string;
  timestamp: string;
  user: User;
  status: string;
  icon: React.ReactNode;
  attachments?: TaskCommentAttachment[];
  reactions?: { [emoji: string]: TaskCommentReaction };
}

interface ProofOfWorkDocument {
  id: string;
  title: string;
  filename: string;
  file_size: number;
  file_type: string;
  uploaded_at: string;
  file_url: string;
}

interface ApprovalHistoryEntry {
  id: string;
  review_action: 'initial_review' | 'approval' | 'rejection' | 're_verification' | 'revision_request' | 'conditional_approval';
  review_sequence: number;
  verification_cycle: number;
  reviewed_at?: string;
  reviewer?: User;
  quality_grade?: string;
  review_notes?: string;
  rejection_reason?: string;
  rejection_details?: string;
}

interface ProofOfWorkSubmission {
  id: string;
  submission_number: number;
  status: 'submitted' | 'under_review' | 'approved' | 'rejected' | 'resubmitted';
  submission_notes: string;
  submitted_at: string;
  reviewed_at?: string;
  reviewed_by?: User;
  review_notes?: string;
  rejection_reason?: string;
  rejection_details?: string;
  approved_at?: string;
  approved_by?: User;
  submitted_by: User;
  documents: ProofOfWorkDocument[];
  assignment: {
    id: string;
    assignee: {
      user: User;
    };
  };
  approval_method?: 'single' | 'multiple_all' | 'multiple_any' | 'average' | 'weighted_average' | 'highest' | 'lowest';
  required_approvers?: string[];
  quality_grade?: string;
  points_awarded?: number;
  approval_history?: ApprovalHistoryEntry[];
}

interface TaskSubmissionProps {
  taskId: string;
  groupId: string;
  userRole?: 'admin' | 'team_lead' | 'member';
  isAdmin?: boolean;
  onTaskUpdate?: () => void;
  task?: {
    approver_ids?: string[];
    approvers?: Array<{ id: string; user?: { id: string } }>;
    approval_type?: string;
    group?: {
      id: string;
      name?: string;
    } | null;
    is_instance?: boolean;
  };
}

const TaskSubmission: React.FC<TaskSubmissionProps> = ({ taskId, groupId, userRole = 'member', isAdmin = false, onTaskUpdate, task: taskProp }) => {
  const { user: currentUser } = useAuth();
  const notificationService = useNotification();
  const confirmService = useConfirm();

  const [submissions, setSubmissions] = useState<ProofOfWorkSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const isLoadingRef = useRef(false);
  const [task, setTask] = useState(taskProp || null);

  // Check if current user is an approver for this task
  const isApprover = React.useMemo(() => {
    if (!currentUser?.id || !task) {
      return false;
    }

    if (task.approval_type === 'none' || !task.approval_type) {
      return false;
    }

    if (task.approval_type === 'single' || task.approval_type === 'multiple') {
      const currentUserId = String(currentUser.id);

      // Check approver_ids first (most reliable)
      const approverIds = task.approver_ids || [];
      if (approverIds.length > 0) {
        const isInApproverIds = approverIds.some((id: any) => {
          const approverIdStr = String(id);
          return approverIdStr === currentUserId || approverIdStr === currentUser.id;
        });
        if (isInApproverIds) {
          console.log('[TaskSubmission] User is approver (via approver_ids)', {
            currentUserId,
            approverIds
          });
          return true;
        }
      }

      // Fallback to checking approvers array
      if (task.approvers && task.approvers.length > 0) {
        const isInApprovers = task.approvers.some((approver: any) => {
          const approverId = approver.user?.id || approver.id;
          const approverIdStr = String(approverId);
          return approverIdStr === currentUserId || approverId === currentUser.id;
        });
        if (isInApprovers) {
          console.log('[TaskSubmission] User is approver (via approvers array)', {
            currentUserId,
            approvers: task.approvers
          });
          return true;
        }
      }
    }

    return false;
  }, [currentUser?.id, task]);

  const [reviewingSubmission, setReviewingSubmission] = useState<string | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [qualityGrade, setQualityGrade] = useState<string>('A');
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectionDetails, setRejectionDetails] = useState('');
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState<ProofOfWorkSubmission | null>(null);
  const [showRejectionForm, setShowRejectionForm] = useState(false);

  // Resubmission states
  const [showResubmissionModal, setShowResubmissionModal] = useState(false);
  const [selectedSubmissionForResubmit, setSelectedSubmissionForResubmit] = useState<ProofOfWorkSubmission | null>(null);
  const [resubmissionNotes, setResubmissionNotes] = useState('');
  const [resubmissionFiles, setResubmissionFiles] = useState<File[]>([]);
  const [isResubmitting, setIsResubmitting] = useState(false);

  // Side panel states
  const [showSubmissionDetails, setShowSubmissionDetails] = useState(false);
  const [selectedSubmissionForDetails, setSelectedSubmissionForDetails] = useState<ProofOfWorkSubmission | null>(null);
  const [submissionTimeline, setSubmissionTimeline] = useState<SubmissionTimelineItem[]>([]);
  const [loadingTimeline, setLoadingTimeline] = useState(false);

  // Load task details if not provided
  useEffect(() => {
    const loadTaskDetails = async () => {
      // If taskProp is provided, use it immediately
      if (taskProp) {
        console.log('[TaskSubmission] Setting task from taskProp:', {
          hasGroup: !!taskProp.group,
          isInstance: taskProp.is_instance,
          groupId: taskProp.group?.id
        });
        setTask(taskProp);
        return;
      }

      // Otherwise, load task details (don't require groupId for instances)
      if (!task && taskId) {
        try {
          console.log('[TaskSubmission] Loading task details:', { taskId, groupId });
          const response = await apiService.getTaskDetails(taskId, { work_mode: 'work' });
          if (response.status === 'success') {
            console.log('[TaskSubmission] Task details loaded:', {
              hasGroup: !!response.data?.group,
              isInstance: response.data?.is_instance,
              groupId: response.data?.group?.id
            });
            setTask(response.data);
          }
        } catch (error: any) {
          console.error('Error loading task details:', error);
          notificationService.error(error.response?.data?.message || error.response?.data?.error || 'Failed to load task details');
        }
      }
    };
    loadTaskDetails();
  }, [taskId, groupId, task, taskProp]);

  // Load submissions based on user role and approver status
  const loadSubmissions = useCallback(async () => {
    if (isLoadingRef.current) return;

    try {
      isLoadingRef.current = true;
      setLoading(true);
      setError(null);

      // Get groupId from task if not provided (for instances)
      const taskObj = task as any;
      const effectiveGroupId = groupId || taskObj?.group?.id || null;

      console.log('[TaskSubmission] loadSubmissions called:', {
        taskId,
        groupId,
        effectiveGroupId,
        taskGroupId: taskObj?.group?.id,
        isInstance: taskObj?.is_instance,
        isApprover,
        isAdmin,
        userRole
      });

      // Allow loading submissions for instances even without groupId
      // The backend will handle instances without group_id
      if (!effectiveGroupId && !taskObj?.is_instance) {
        console.warn('[TaskSubmission] No groupId and task is not an instance, but proceeding anyway (backend will handle)');
        // Don't return early - let the API call proceed, backend will handle it
      }

      console.log('[TaskSubmission] Proceeding with API call:', {
        taskId,
        effectiveGroupId,
        isInstance: taskObj?.is_instance,
        willProceed: true
      });

      let response;
      // If user is an approver, use getUserSubmissions which will return all submissions for approvers
      // If user is admin/team_lead, use getTaskSubmissions to see all submissions
      // Otherwise, use getUserSubmissions to see only their own submissions
      if (isApprover) {
        // Approvers see all submissions via getUserSubmissions (backend handles this)
        response = await apiService.getUserSubmissions(taskId, effectiveGroupId, currentUser?.id) as ApiResponse;
      } else if (isAdmin || userRole === 'admin' || userRole === 'team_lead') {
        // Admin/Team Lead sees all submissions for the task
        response = await apiService.getTaskSubmissions(taskId, effectiveGroupId) as ApiResponse;
      } else {
        // Member sees only their own submissions
        response = await apiService.getUserSubmissions(taskId, effectiveGroupId, currentUser?.id) as ApiResponse;
      }

      if (response.status === 'success') {
        setSubmissions(response.data || []);
        setIsInitialLoad(false);
      } else {
        setError(response.message || 'Failed to load submissions');
        setIsInitialLoad(false);
        notificationService.error(response.message || 'Failed to load submissions');
      }
    } catch (err: any) {
      console.error('Error loading submissions:', err);
      setError(err);
      setIsInitialLoad(false);
      notificationService.error(err.response?.data?.message || err.response?.data?.error || 'Failed to load submissions');
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [taskId, groupId, task, isAdmin, userRole, currentUser?.id, notificationService, isApprover]);

  // Load submission timeline and details
  const loadSubmissionDetails = useCallback(async (submission: ProofOfWorkSubmission) => {
    try {
      setLoadingTimeline(true);
      setSelectedSubmissionForDetails(submission);

      // Get groupId from task if not provided (for instances)
      const effectiveGroupId = groupId || (task as any)?.group?.id || null;

      // Get all submissions from this specific user for this task
      const userSubmissionsResponse = await apiService.getUserSubmissions(taskId, effectiveGroupId, submission.submitted_by.id);

      if (userSubmissionsResponse.status === 'success') {
        let userSubmissions = userSubmissionsResponse.data || [];

        // Merge approval_history from the passed submission if it exists
        const submissionWithHistory = userSubmissions.find((sub: ProofOfWorkSubmission) => sub.id === submission.id);
        if (submissionWithHistory && submission.approval_history) {
          submissionWithHistory.approval_history = submission.approval_history;
        } else if (!submissionWithHistory && submission.approval_history) {
          // If submission not found in response, add it with its approval_history
          userSubmissions = [...userSubmissions, { ...submission }];
        }

        // Create timeline from user's submission history
        const timeline: SubmissionTimelineItem[] = [];

        console.log(`Found ${userSubmissions.length} submissions for user ${submission.submitted_by.full_name}`);

        // Sort submissions by submission number (1st, 2nd, 3rd, etc.)
        const sortedSubmissions = [...userSubmissions].sort((a, b) => a.submission_number - b.submission_number);

        // Show all submissions up to and including the selected one
        const selectedSubmissionNumber = submission.submission_number;
        const submissionsToShow = sortedSubmissions.filter(sub => sub.submission_number <= selectedSubmissionNumber);

        console.log(`Showing submissions up to #${selectedSubmissionNumber}: ${submissionsToShow.length} submissions`);

        submissionsToShow.forEach((userSubmission, index) => {
          const isSelectedSubmission = userSubmission.id === submission.id;
          // Add submission event
          timeline.push({
            id: `submission-${userSubmission.id}`,
            type: 'proof_submitted',
            title: `Submission #${userSubmission.submission_number}`,
            description: userSubmission.submission_notes || `Proof of work submitted (Attempt #${userSubmission.submission_number})`,
            timestamp: userSubmission.submitted_at,
            user: userSubmission.submitted_by,
            status: 'submitted',
            icon: (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14,2 14,8 20,8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
              </svg>
            ),
            attachments: [],
            reactions: {}
          });

          // Add all approval history entries for this submission
          // For the selected submission, show all history; for previous submissions, show summary
          if (userSubmission.approval_history && userSubmission.approval_history.length > 0) {
            // Sort approval history by verification cycle and review sequence
            const sortedHistory = [...userSubmission.approval_history].sort((a, b) => {
              if (a.verification_cycle !== b.verification_cycle) {
                return a.verification_cycle - b.verification_cycle;
              }
              return a.review_sequence - b.review_sequence;
            });

            sortedHistory.forEach((historyEntry: ApprovalHistoryEntry) => {
              const reviewerName = historyEntry.reviewer?.full_name ||
                (historyEntry.reviewer?.first_name && historyEntry.reviewer?.last_name
                  ? `${historyEntry.reviewer.first_name} ${historyEntry.reviewer.last_name}`
                  : 'Unknown Reviewer');
              const timestamp = historyEntry.reviewed_at || userSubmission.submitted_at;


              if (historyEntry.review_action === 'approval' || historyEntry.review_action === 'initial_review') {
                const gradePercentage = historyEntry.quality_grade ? getQualityGradePercentage(historyEntry.quality_grade) : null;
                const qualityInfo = historyEntry.quality_grade
                  ? ` (Grade: ${historyEntry.quality_grade}${gradePercentage !== null ? ` - ${gradePercentage}%` : ''})`
                  : '';
                timeline.push({
                  id: `approval-${historyEntry.id}`,
                  type: 'approval',
                  title: `Approved by ${reviewerName}${qualityInfo}`,
                  description: historyEntry.review_notes || `Approved by ${reviewerName}${qualityInfo}`,
                  timestamp: timestamp,
                  user: historyEntry.reviewer || userSubmission.submitted_by,
                  status: 'approved',
                  icon: (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                      <polyline points="22,4 12,14.01 9,11.01" />
                    </svg>
                  ),
                  attachments: [],
                  reactions: {}
                });
              } else if (historyEntry.review_action === 'rejection') {
                const rejectionReasonText = historyEntry.rejection_reason ? ` - ${historyEntry.rejection_reason}` : '';
                timeline.push({
                  id: `rejection-${historyEntry.id}`,
                  type: 'rejection',
                  title: `Rejected by ${reviewerName}${rejectionReasonText}`,
                  description: historyEntry.rejection_details || historyEntry.review_notes || `Rejected by ${reviewerName}`,
                  timestamp: timestamp,
                  user: historyEntry.reviewer || userSubmission.submitted_by,
                  status: 'rejected',
                  icon: (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="15" y1="9" x2="9" y2="15" />
                      <line x1="9" y1="9" x2="15" y2="15" />
                    </svg>
                  ),
                  attachments: [],
                  reactions: {}
                });
              } else if (historyEntry.review_action === 're_verification') {
                timeline.push({
                  id: `reverification-${historyEntry.id}`,
                  type: 'resubmission',
                  title: `Re-verified by ${reviewerName}`,
                  description: historyEntry.review_notes || `Re-verified by ${reviewerName}`,
                  timestamp: timestamp,
                  user: historyEntry.reviewer || userSubmission.submitted_by,
                  status: 'submitted',
                  icon: (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14,2 14,8 20,8" />
                      <line x1="16" y1="13" x2="8" y2="13" />
                      <line x1="16" y1="17" x2="8" y2="17" />
                    </svg>
                  ),
                  attachments: [],
                  reactions: {}
                });
              }
            });
          } else {
            // Fallback: If no approval history, show final status
            if (userSubmission.status === 'approved') {
              timeline.push({
                id: `approval-${userSubmission.id}`,
                type: 'approval',
                title: `Submission #${userSubmission.submission_number} Approved`,
                description: userSubmission.review_notes || `Submission #${userSubmission.submission_number} has been approved`,
                timestamp: userSubmission.approved_at || userSubmission.reviewed_at || userSubmission.submitted_at,
                user: userSubmission.approved_by || userSubmission.reviewed_by || userSubmission.submitted_by,
                status: 'approved',
                icon: (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22,4 12,14.01 9,11.01" />
                  </svg>
                ),
                attachments: [],
                reactions: {}
              });
            } else if (userSubmission.status === 'rejected') {
              timeline.push({
                id: `rejection-${userSubmission.id}`,
                type: 'rejection',
                title: `Submission #${userSubmission.submission_number} Rejected`,
                description: userSubmission.rejection_details || userSubmission.review_notes || `Submission #${userSubmission.submission_number} has been rejected`,
                timestamp: userSubmission.reviewed_at || userSubmission.submitted_at,
                user: userSubmission.reviewed_by || userSubmission.submitted_by,
                status: 'rejected',
                icon: (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                  </svg>
                ),
                attachments: [],
                reactions: {}
              });
            }
          }
        });

        // Sort timeline by timestamp (oldest first)
        timeline.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

        setSubmissionTimeline(timeline);
      } else {
        // Fallback to basic timeline if API fails
        const basicTimeline: SubmissionTimelineItem[] = [
          {
            id: 'submitted',
            type: 'proof_submitted',
            title: 'Proof of Work Submitted',
            description: submission.submission_notes || 'Proof of work has been submitted for review',
            timestamp: submission.submitted_at,
            user: submission.submitted_by,
            status: 'submitted',
            icon: (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14,2 14,8 20,8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
              </svg>
            ),
            attachments: [],
            reactions: {}
          }
        ];

        if (submission.status === 'approved') {
          basicTimeline.push({
            id: 'approved',
            type: 'approval',
            title: 'Approved',
            description: submission.review_notes || 'Submission has been approved and completed',
            timestamp: submission.reviewed_at || submission.approved_at || submission.submitted_at,
            user: submission.reviewed_by || submission.approved_by || submission.submitted_by,
            status: 'approved',
            icon: (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22,4 12,14.01 9,11.01" />
              </svg>
            ),
            attachments: [],
            reactions: {}
          });
        }

        setSubmissionTimeline(basicTimeline);
      }

      setShowSubmissionDetails(true);
    } catch (error: any) {
      console.error('Error loading submission details:', error);
      notificationService.error(error.response?.data?.message || error.response?.data?.error || 'Failed to load submission details');

      const fallbackTimeline: SubmissionTimelineItem[] = [
        {
          id: 'submitted',
          type: 'proof_submitted',
          title: 'Proof of Work Submitted',
          description: submission.submission_notes || 'Proof of work has been submitted for review',
          timestamp: submission.submitted_at,
          user: submission.submitted_by,
          status: 'submitted',
          icon: (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14,2 14,8 20,8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
            </svg>
          ),
          attachments: [],
          reactions: {}
        }
      ];

      setSubmissionTimeline(fallbackTimeline);
      setShowSubmissionDetails(true);
    } finally {
      setLoadingTimeline(false);
    }
  }, [notificationService, taskId, groupId, task]);

  const closeSubmissionDetails = () => {
    setShowSubmissionDetails(false);
    setSelectedSubmissionForDetails(null);
    setSubmissionTimeline([]);
    setShowRejectionForm(false);
    setRejectionReason('');
    setRejectionDetails('');
    setReviewNotes('');
    setQualityGrade('A'); // Reset to default
  };

  useEffect(() => {
    // Load submissions if we have taskId and it's the initial load
    // The backend will handle instances without groupId
    const taskObj = task as any;
    const hasGroupId = groupId && groupId.trim() !== '';
    const hasTaskGroup = taskObj?.group?.id;
    const isInstance = taskObj?.is_instance;

    console.log('[TaskSubmission] useEffect check:', {
      taskId,
      groupId,
      hasGroupId,
      hasTaskGroup,
      isInstance,
      hasTask: !!task,
      isInitialLoad
    });

    // Simply load if we have taskId - backend will handle groupId requirement
    if (taskId && isInitialLoad) {
      console.log('[TaskSubmission] Triggering loadSubmissions - taskId present');
      loadSubmissions();
    } else {
      console.log('[TaskSubmission] Not loading submissions:', {
        hasTaskId: !!taskId,
        isInitialLoad
      });
    }
  }, [taskId, groupId, isInitialLoad, loadSubmissions, task]);

  const handleApproveSubmission = async (submission: ProofOfWorkSubmission) => {
    if (!isApprover && !isAdmin && userRole !== 'admin' && userRole !== 'team_lead') return;

    const qualityPercentage = getQualityGradePercentage(qualityGrade);
    const confirmed = await confirmService.show(
      'Approve Submission',
      `Are you sure you want to approve ${submission.submitted_by.full_name}'s submission #${submission.submission_number} with quality grade ${qualityGrade} (${qualityPercentage}% points)?`,
      { okText: 'Approve', cancelText: 'Cancel' }
    );

    if (!confirmed) return;

    try {
      const response = await apiService.approveProofOfWork(submission.id, reviewNotes, qualityGrade) as ApiResponse;

      if (response.status === 'success') {
        const allApprovalsMet = response.data?.all_approvals_met ?? false;

        if (allApprovalsMet) {
          const finalPoints = response.data?.points_awarded || response.data?.base_points ? Math.round(response.data.base_points * (qualityPercentage / 100)) : 0;
          notificationService.success(`Submission fully approved by all reviewers! Final grade: ${response.data?.quality_grade || qualityGrade} (${finalPoints} points)`);
        } else {
          notificationService.success(`Your approval has been recorded. Waiting for other approvers to review. Current grade: ${qualityGrade} (${qualityPercentage}%)`);
        }

        setReviewNotes('');
        setQualityGrade('A'); // Reset to default
        loadSubmissions(); // Reload submissions
        if (onTaskUpdate) {
          onTaskUpdate(); // Notify parent to refresh task data
        }
      } else {
        // Enhanced error message for hierarchical permission issues
        const errorMsg = response.message || 'Failed to approve submission';
        if (errorMsg.toLowerCase().includes('hierarchical') || errorMsg.toLowerCase().includes('higher rank')) {
          notificationService.error(`Permission Denied: ${errorMsg}`);
          // Also show in console for debugging
          console.warn('Hierarchical permission error:', {
            submissionId: submission.id,
            currentUser: currentUser?.email,
            approvedBy: submission.approved_by?.email,
            error: errorMsg
          });
        } else {
          notificationService.error(errorMsg);
        }
      }
    } catch (error: any) {
      console.error('Error approving submission:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to approve submission';
      notificationService.error(`Error: ${errorMessage}`);
    }
  };

  const handleRejectSubmission = async (submission: ProofOfWorkSubmission) => {
    if (!isApprover && !isAdmin && userRole !== 'admin' && userRole !== 'team_lead') return;

    const confirmed = await confirmService.show(
      'Reject Submission',
      `Are you sure you want to reject ${submission.submitted_by.full_name}'s submission #${submission.submission_number}?`,
      { okText: 'Reject', cancelText: 'Cancel' }
    );

    if (!confirmed) return;

    try {
      const response = await apiService.rejectProofOfWork(
        submission.id,
        rejectionReason,
        rejectionDetails,
        reviewNotes
      ) as ApiResponse;

      if (response.status === 'success') {
        notificationService.success('Submission rejected successfully');
        setRejectionReason('');
        setRejectionDetails('');
        setReviewNotes('');
        loadSubmissions(); // Reload submissions
        if (onTaskUpdate) {
          onTaskUpdate(); // Notify parent to refresh task data
        }
      } else {
        // Enhanced error message for hierarchical permission issues
        const errorMsg = response.message || 'Failed to reject submission';
        if (errorMsg.toLowerCase().includes('hierarchical') || errorMsg.toLowerCase().includes('higher rank')) {
          notificationService.error(`Permission Denied: ${errorMsg}`);
          console.warn('Hierarchical permission error:', {
            submissionId: submission.id,
            currentUser: currentUser?.email,
            approvedBy: submission.approved_by?.email,
            error: errorMsg
          });
        } else {
          notificationService.error(errorMsg);
        }
      }
    } catch (error: any) {
      console.error('Error rejecting submission:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to reject submission';
      notificationService.error(`Error: ${errorMessage}`);
    }
  };

  const openReviewModal = (submission: ProofOfWorkSubmission) => {
    setSelectedSubmission(submission);
    setShowReviewModal(true);
    setReviewNotes('');
    setQualityGrade('A'); // Reset to default
    setRejectionReason('');
    setRejectionDetails('');
  };

  const closeReviewModal = () => {
    setShowReviewModal(false);
    setSelectedSubmission(null);
    setReviewNotes('');
    setQualityGrade('A'); // Reset to default
    setRejectionReason('');
    setRejectionDetails('');
  };

  const showRejectionFormInSidePanel = () => {
    setShowRejectionForm(true);
    setRejectionReason('');
    setRejectionDetails('');
    setReviewNotes('');
    setQualityGrade('A'); // Reset to default
  };

  const cancelRejection = () => {
    setShowRejectionForm(false);
    setRejectionReason('');
    setRejectionDetails('');
    setReviewNotes('');
    setQualityGrade('A'); // Reset to default
  };

  const handleRejectFromSidePanel = async () => {
    if (!selectedSubmissionForDetails) return;

    if (!rejectionReason) {
      notificationService.error('Please select a rejection reason');
      return;
    }

    if (!rejectionDetails.trim()) {
      notificationService.error('Please provide rejection details');
      return;
    }

    try {
      const response = await apiService.rejectProofOfWork(
        selectedSubmissionForDetails.id,
        rejectionReason,
        rejectionDetails,
        reviewNotes
      ) as ApiResponse;

      if (response.status === 'success') {
        notificationService.success('Submission rejected successfully');
        setRejectionReason('');
        setRejectionDetails('');
        setReviewNotes('');
        setShowRejectionForm(false);
        closeSubmissionDetails();
        loadSubmissions(); // Reload submissions
        if (onTaskUpdate) {
          onTaskUpdate(); // Notify parent to refresh task data
        }
      } else {
        // Enhanced error message for hierarchical permission issues
        const errorMsg = response.message || 'Failed to reject submission';
        if (errorMsg.toLowerCase().includes('hierarchical') || errorMsg.toLowerCase().includes('higher rank')) {
          notificationService.error(`Permission Denied: ${errorMsg}`);
        } else {
          notificationService.error(errorMsg);
        }
      }
    } catch (error: any) {
      console.error('Error rejecting submission:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to reject submission';
      notificationService.error(`Error: ${errorMessage}`);
    }
  };

  const getFileIcon = (fileType: string, filename?: string) => {
    const lowerFileType = fileType.toLowerCase();
    const lowerFilename = filename?.toLowerCase() || '';

    if (lowerFileType.includes('pdf') || lowerFilename.endsWith('.pdf')) {
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="#ef4444" className={styles.fileIcon}>
          <path d="M5.523 10.424q.21-.124.459-.238a8 8 0 0 1-.45.606c-.28.337-.498.516-.635.572l-.035.012a.3.3 0 0 1-.026-.044c-.056-.11-.054-.216.04-.36.106-.165.319-.354.647-.548m2.455-1.647q-.178.037-.356.078a21 21 0 0 0 .5-1.05 12 12 0 0 0 .51.858q-.326.048-.654.114m2.525.939a4 4 0 0 1-.435-.41q.344.007.612.054c.317.057.466.147.518.209a.1.1 0 0 1 .026.064.44.44 0 0 1-.06.2.3.3 0 0 1-.094.124.1.1 0 0 1-.069.015c-.09-.003-.258-.066-.498-.256M8.278 4.97c-.04.244-.108.524-.2.829a5 5 0 0 1-.089-.346c-.076-.353-.087-.63-.046-.822.038-.177.11-.248.196-.283a.5.5 0 0 1 .145-.04c.013.03.028.092.032.198q.008.183-.038.465z" />
          <path d="M4 0h8a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2m.165 11.668c.09.18.23.343.438.419.207.075.412.04.58-.03.318-.13.635-.436.926-.786.333-.401.683-.927 1.021-1.51a11.6 11.6 0 0 1 1.997-.406c.3.383.61.713.91.95.28.22.603.403.934.417a.86.86 0 0 0 .51-.138c.155-.101.27-.247.354-.416.09-.181.145-.37.138-.563a.84.84 0 0 0-.2-.518c-.226-.27-.596-.4-.96-.465a5.8 5.8 0 0 0-1.335-.05 11 11 0 0 1-.98-1.686c.25-.66.437-1.284.52-1.794.036-.218.055-.426.048-.614a1.24 1.24 0 0 0-.127-.538.7.7 0 0 0-.477-.365c-.202-.043-.41 0-.601.077-.377.15-.576.47-.651.823-.073.34-.04.736.046 1.136.088.406.238.848.43 1.295a20 20 0 0 1-1.062 2.227 7.7 7.7 0 0 0-1.482.645c-.37.22-.699.48-.897.787-.21.326-.275.714-.08 1.103" />
        </svg>
      );
    } else if (lowerFileType.includes('excel') || lowerFileType.includes('spreadsheet') || lowerFilename.endsWith('.xlsx') || lowerFilename.endsWith('.xls')) {
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="#10b981" className={styles.fileIcon}>
          <path d="M6 12v-2h3v2z" />
          <path d="M9.293 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4.707A1 1 0 0 0 13.707 4L10 .293A1 1 0 0 0 9.293 0M9.5 3.5v-2l3 3h-2a1 1 0 0 1-1-1M3 9h10v1h-3v2h3v1h-3v2H9v-2H6v2H5v-2H3v-1h2v-2H3z" />
        </svg>
      );
    } else if (lowerFileType.includes('word') || lowerFileType.includes('document') || lowerFilename.endsWith('.docx') || lowerFilename.endsWith('.doc')) {
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="#3b82f6" className={styles.fileIcon}>
          <path d="M12 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2M5.485 4.879l1.036 4.144.997-3.655a.5.5 0 0 1 .964 0l.997 3.655 1.036-4.144a.5.5 0 0 1 .97.242l-1.5 6a.5.5 0 0 1-.967.01L8 7.402l-1.018 3.73a.5.5 0 0 1-.967-.01l-1.5-6a.5.5 0 1 1 .97-.242z" />
        </svg>
      );
    } else if (lowerFileType.includes('image') || lowerFilename.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/)) {
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="#8b5cf6" className={styles.fileIcon}>
          <path d="M8.002 5.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0" />
          <path d="M12 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2M3 2a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v8l-2.083-2.083a.5.5 0 0 0-.76.063L8 11 5.835 9.7a.5.5 0 0 0-.611.076L3 12z" />

        </svg>
      );
    } else if (lowerFileType.includes('zip') || lowerFileType.includes('archive') || lowerFilename.match(/\.(zip|rar|7z|tar|gz)$/)) {
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="#f59e0b" className={styles.fileIcon}>
          <path d="M6.5 7.5a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v.938l.4 1.599a1 1 0 0 1-.416 1.074l-.93.62a1 1 0 0 1-1.109 0l-.93-.62a1 1 0 0 1-.415-1.074l.4-1.599zm2 0h-1v.938a1 1 0 0 1-.03.243l-.4 1.598.93.62.93-.62-.4-1.598a1 1 0 0 1-.03-.243z" />
          <path d="M2 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2zm5.5-1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H9v1H8v1h1v1H8v1h1v1H7.5V5h-1V4h1V3h-1V2h1z" />
        </svg>
      );
    } else {
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="#6b7280" className={styles.fileIcon}>
          <path d="M4 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2zm0 1h8a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1" />
        </svg>
      );
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDocumentUrl = (doc: ProofOfWorkDocument): string => {
    if (!doc.file_url) {
      return '';
    }

    if (doc.file_url.startsWith('http://') || doc.file_url.startsWith('https://')) {
      return doc.file_url;
    }

    const baseUrl = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || 'https://demo.api.workwallet';
    return `${baseUrl}${doc.file_url}`;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Rejection reason options for CustomDropdown
  const rejectionReasonOptions: DropdownOption[] = [
    { value: 'insufficient_evidence', label: 'Insufficient Evidence' },
    { value: 'poor_quality', label: 'Poor Quality' },
    { value: 'wrong_format', label: 'Wrong Format' },
    { value: 'missing_information', label: 'Missing Information' },
    { value: 'not_related_to_task', label: 'Not Related to Task' },
    { value: 'other', label: 'Other' }
  ];

  // Quality grade options based on AICTE and Industry Standards
  const qualityGradeOptions: DropdownOption[] = [
    { value: 'A', label: 'A - Excellent (100%) - Exceeds all expectations, outstanding quality' },
    { value: 'B', label: 'B - Good (80%) - Meets expectations, good quality work' },
    { value: 'C', label: 'C - Satisfactory (60%) - Meets minimum requirements, acceptable quality' },
    { value: 'D', label: 'D - Needs Improvement (40%) - Below expectations, requires significant improvement' },
    { value: 'F', label: 'F - Fail (0%) - Does not meet requirements, unacceptable quality' }
  ];

  // Get quality grade percentage
  const getQualityGradePercentage = (grade: string): number => {
    const gradeMap: { [key: string]: number } = {
      'A': 100,
      'B': 80,
      'C': 60,
      'D': 40,
      'F': 0
    };
    return gradeMap[grade] || 100;
  };

  // Convert rejection reason value to display text
  const getRejectionReasonDisplayText = (reasonValue: string): string => {
    const option = rejectionReasonOptions.find(opt => opt.value === reasonValue);
    return option ? option.label : reasonValue;
  };


  const handleViewDocument = async (doc: ProofOfWorkDocument) => {
    try {
      const fileUrl = getDocumentUrl(doc);
      if (!fileUrl) {
        notificationService.error('File URL not available');
        return;
      }

      console.log('Viewing document:', {
        filename: doc.filename,
        fileType: doc.file_type,
        fileUrl: fileUrl,
        originalUrl: doc.file_url
      });

      // Check if it's a PDF or image that can be viewed in browser
      const lowerFileType = doc.file_type.toLowerCase();
      const lowerFilename = doc.filename.toLowerCase();

      if (lowerFileType.includes('pdf') || lowerFilename.endsWith('.pdf')) {
        // Open PDF in new tab
        window.open(fileUrl, '_blank');
        notificationService.success(`Opening ${doc.filename} in new tab`);
      } else if (lowerFileType.includes('image') || lowerFilename.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/)) {
        // Open image in new tab
        window.open(fileUrl, '_blank');
        notificationService.success(`Opening ${doc.filename} in new tab`);
      } else {
        // For other file types, try to download
        handleDownloadDocument(doc);
      }
    } catch (error: any) {
      console.error('Error viewing document:', error);
      notificationService.error(error.response?.data?.message || error.response?.data?.error || 'Failed to view document');
    }
  };

  const handleDownloadDocument = async (doc: ProofOfWorkDocument) => {
    try {
      const fileUrl = getDocumentUrl(doc);
      if (!fileUrl) {
        notificationService.error('File URL not available');
        return;
      }

      console.log('Downloading document:', {
        filename: doc.filename,
        fileType: doc.file_type,
        fileUrl: fileUrl,
        originalUrl: doc.file_url
      });

      // Create a temporary link element for download
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = doc.filename;
      link.target = '_blank';

      // Add to DOM, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      notificationService.success(`Downloading ${doc.filename}`);
    } catch (error: any) {
      console.error('Error downloading document:', error);
      notificationService.error(error.response?.data?.message || error.response?.data?.error || 'Failed to download document');
    }
  };

  if (isInitialLoad) {
    return (
      <div className={styles.tabContent}>
        <SkeletonLoader count={3} height="120px" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.tabContent}>
        <EmptyState
          title="Failed to Load Submissions"
          description="Unable to load submissions. Please check your connection and try again."
          action={{
            label: "Retry Loading",
            onClick: () => loadSubmissions()
          }}
        />
      </div>
    );
  }

  return (
    <div className={styles.tabContent}>
      <div className={styles.submissionsSection}>
        {submissions.length > 0 ? (
          <div className={styles.submissionsList}>
            {submissions.map((submission) => (
              <div key={submission.id} className={styles.submissionCard}>
                <div className={styles.submissionHeader}>
                  <div className={styles.submissionInfo}>
                    <div className={styles.submissionTitle}>
                      Submission #{submission.submission_number}
                      {(isApprover || isAdmin || userRole === 'admin' || userRole === 'team_lead') && (
                        <span className={styles.submitterName}>
                          by {submission.submitted_by.full_name}
                        </span>
                      )}
                    </div>
                    <div className={styles.submissionMeta}>
                      <span className={styles.submissionDate}>
                        Submitted on {formatDate(submission.submitted_at)}
                      </span>
                      {submission.reviewed_at && (
                        <span className={styles.reviewDate}>
                          Reviewed on {formatDate(submission.reviewed_at)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className={styles.submissionStatus}>
                    <StatusBadge status={
                      submission.status === 'submitted' || submission.status === 'under_review' || submission.status === 'resubmitted'
                        ? 'pending_approval'
                        : submission.status === 'approved'
                          ? 'approved'
                          : submission.status === 'rejected'
                            ? 'rejected'
                            : 'pending_approval'
                    } />
                    {submission.status === 'under_review' && submission.approval_method === 'multiple_all' && submission.required_approvers && (
                      <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#6b7280' }}>
                        Waiting for all approvers...
                      </div>
                    )}
                  </div>
                </div>

                {submission.submission_notes && (
                  <div className={styles.submissionNotes}>
                    <h4>Submission Notes:</h4>
                    <p>{submission.submission_notes}</p>
                  </div>
                )}

                {submission.review_notes && (
                  <div className={styles.reviewNotes}>
                    <h4>Review Notes:</h4>
                    <p>{submission.review_notes}</p>
                  </div>
                )}

                {submission.rejection_reason && (
                  <div className={styles.rejectionInfo}>
                    <h4>Rejection Reason:</h4>
                    <p><strong>{getRejectionReasonDisplayText(submission.rejection_reason)}</strong></p>
                    {submission.rejection_details && (
                      <p>{submission.rejection_details}</p>
                    )}
                  </div>
                )}

                <div className={styles.submissionActions}>
                  {(isApprover || isAdmin || userRole === 'admin' || userRole === 'team_lead') ? (
                    <button
                      className={`${buttonStyles.btn} ${buttonStyles.btnGreenSecondary}`}
                      onClick={() => loadSubmissionDetails(submission)}
                    >
                      Review Submission
                    </button>
                  ) : (
                    <button
                      className={`${buttonStyles.btn} ${buttonStyles.btnSecondary}`}
                      onClick={() => loadSubmissionDetails(submission)}
                    >
                      View Submission Details
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            title={isAdmin ? "No Submissions Yet" : "No Submissions"}
            description={
              isAdmin
                ? "No team members have submitted their work for this task yet."
                : "You haven't submitted any proof of work for this task yet."
            }
          />
        )}
      </div>

      {/* Review Modal */}
      {showReviewModal && selectedSubmission && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3>Review Submission #{selectedSubmission.submission_number}</h3>
              <button
                className={styles.closeButton}
                onClick={closeReviewModal}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className={styles.modalBody}>
              <div className={styles.reviewForm}>
                <div className={styles.formGroup}>
                  <label>Quality Grade <span style={{ color: '#ef4444' }}>*</span></label>
                  <CustomDropdown
                    options={qualityGradeOptions}
                    value={qualityGrade}
                    onChange={(value) => setQualityGrade(value as string)}
                    placeholder="Select quality grade..."
                    size="md"
                    variant="outlined"
                    className={customDropdownStyles.sizeFull}
                  />
                  <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
                    Selected grade: <strong>{qualityGrade}</strong> - {getQualityGradePercentage(qualityGrade)}% of assigned points will be awarded
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label>Review Notes (Optional)</label>
                  <textarea
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                    placeholder="Add your review comments..."
                    rows={3}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label>Rejection Reason (if rejecting)</label>
                  <select
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                  >
                    <option value="">Select a reason...</option>
                    <option value="insufficient_evidence">Insufficient Evidence</option>
                    <option value="poor_quality">Poor Quality</option>
                    <option value="wrong_format">Wrong Format</option>
                    <option value="missing_information">Missing Information</option>
                    <option value="not_related_to_task">Not Related to Task</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {rejectionReason && (
                  <div className={styles.formGroup}>
                    <label>Rejection Details</label>
                    <textarea
                      value={rejectionDetails}
                      onChange={(e) => setRejectionDetails(e.target.value)}
                      placeholder="Provide detailed feedback for improvement..."
                      rows={3}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className={styles.modalActions}>
              <button
                className={`${buttonStyles.btn} ${buttonStyles.btnSecondary}`}
                onClick={closeReviewModal}
              >
                Cancel
              </button>
              {rejectionReason ? (
                <button
                  className={`${buttonStyles.btn} ${buttonStyles.btnDanger}`}
                  onClick={() => {
                    handleRejectSubmission(selectedSubmission);
                    closeReviewModal();
                  }}
                >
                  Reject Submission
                </button>
              ) : (
                <button
                  className={`${buttonStyles.btn} ${buttonStyles.btnGreenSecondary}`}
                  onClick={() => {
                    if (!qualityGrade) {
                      notificationService.error('Please select a quality grade before approving');
                      return;
                    }
                    handleApproveSubmission(selectedSubmission);
                    closeReviewModal();
                  }}
                >
                  Approve Submission ({qualityGrade} - {getQualityGradePercentage(qualityGrade)}%)
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Submission Details Side Panel */}
      <SidePanel
        isOpen={showSubmissionDetails}
        onClose={closeSubmissionDetails}
        title={`Submission #${selectedSubmissionForDetails?.submission_number || ''}`}
        icon={
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-check-circle" viewBox="0 0 16 16">
            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
            <path d="m10.97 4.97-.02.022-3.473 4.425-2.093-2.094a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-1.071-1.05" />
          </svg>
        }
      >
        {selectedSubmissionForDetails && (
          <div className={sidePanelStyles.form} style={{ padding: '0 1.5rem' }}>
            {/* Submission Information */}
            <div className={sidePanelStyles.formGroup}>
              <div className={sidePanelStyles.sectionHeader}>
                <h4 className={sidePanelStyles.sectionTitleSmall}>Submission Information</h4>
              </div>
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Submitted by:</span>
                  <span className={styles.infoValue}>{selectedSubmissionForDetails.submitted_by.full_name}</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Submitted on:</span>
                  <span className={styles.infoValue}>{formatDate(selectedSubmissionForDetails.submitted_at)}</span>
                </div>
                {selectedSubmissionForDetails.reviewed_at && (
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Reviewed on:</span>
                    <span className={styles.infoValue}>{formatDate(selectedSubmissionForDetails.reviewed_at)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Submission Notes */}
            {selectedSubmissionForDetails.submission_notes && (
              <div className={sidePanelStyles.formGroup}>
                <div className={sidePanelStyles.sectionHeader}>
                  <h4 className={sidePanelStyles.sectionTitleSmall}>Submission Notes</h4>
                </div>
                <div className={styles.notesContent}>
                  <p>{selectedSubmissionForDetails.submission_notes}</p>
                </div>
              </div>
            )}

            {/* Current Approval Status - Show for approved submissions */}
            {selectedSubmissionForDetails.status === 'approved' && selectedSubmissionForDetails.quality_grade && (
              <div className={sidePanelStyles.formGroup}>
                <div className={sidePanelStyles.sectionHeader}>
                  <h4 className={sidePanelStyles.sectionTitleSmall}>Current Approval Status</h4>
                </div>
                <div className={styles.infoGrid}>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Quality Grade:</span>
                    <span className={styles.infoValue}>
                      <strong>{selectedSubmissionForDetails.quality_grade}</strong> - {getQualityGradePercentage(selectedSubmissionForDetails.quality_grade)}%
                    </span>
                  </div>
                  {selectedSubmissionForDetails.points_awarded !== undefined && (
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}>Points Awarded:</span>
                      <span className={styles.infoValue}><strong>{selectedSubmissionForDetails.points_awarded}</strong> points</span>
                    </div>
                  )}
                  {selectedSubmissionForDetails.approved_by && (
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}>Approved by:</span>
                      <span className={styles.infoValue}>{selectedSubmissionForDetails.approved_by.full_name}</span>
                    </div>
                  )}
                  {selectedSubmissionForDetails.approved_at && (
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}>Approved on:</span>
                      <span className={styles.infoValue}>{formatDate(selectedSubmissionForDetails.approved_at)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Timeline */}
            <div className={sidePanelStyles.formGroup}>
              <div className={sidePanelStyles.sectionHeader}>
                <h4 className={sidePanelStyles.sectionTitleSmall}>Timeline</h4>
              </div>
              {loadingTimeline ? (
                <div className={sidePanelStyles.loadingState}>
                  <div className={sidePanelStyles.loadingSpinner}></div>
                  <span>Loading timeline...</span>
                </div>
              ) : (
                <div className={styles.timeline}>
                  {submissionTimeline.map((item, index) => {
                    const isCurrent = index === submissionTimeline.length - 1; // Last item is current
                    return (
                      <div key={item.id} className={styles.timelineItem}>
                        <div className={styles.timelineMarker}>
                          <div className={`${styles.timelineDot} ${styles[item.status]} ${isCurrent ? styles.current : ''}`}></div>
                          {index < submissionTimeline.length - 1 && <div className={styles.timelineLine}></div>}
                        </div>
                        <div className={styles.timelineContent}>
                          <div className={styles.timelineTitle}>
                            <span className={styles.timelineTitleIcon}>{item.icon}</span>
                            {item.title}
                          </div>
                          <div className={styles.timelineDescription}>{item.description}</div>
                          <div className={styles.timelineMeta}>
                            <span className={styles.timelineUser}>{item.user?.full_name}</span>
                            <span className={styles.timelineDate}>{formatDate(item.timestamp)}</span>
                          </div>
                          <div className={`${styles.timelineStatus} ${styles[item.status]}`}>
                            {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Documents */}
            <div className={sidePanelStyles.formGroup}>
              <div className={sidePanelStyles.sectionHeader}>
                <h4 className={sidePanelStyles.sectionTitleSmall}>Attached Documents</h4>
              </div>
              {selectedSubmissionForDetails.documents && selectedSubmissionForDetails.documents.length > 0 ? (
                <div className={styles.documentsList}>
                  {selectedSubmissionForDetails.documents.map((doc) => (
                    <div key={doc.id} className={styles.documentCard}>
                      <div className={styles.documentIcon}>{getFileIcon(doc.file_type, doc.filename)}</div>
                      <div className={styles.documentInfo}>
                        <div className={styles.documentName}>{doc.title || doc.filename}</div>
                        <div className={styles.documentMeta}>
                          <span>{formatFileSize(doc.file_size)}</span>
                          <span>•</span>
                          <span>{doc.file_type}</span>
                        </div>
                      </div>
                      <div className={styles.documentActions}>
                        <button
                          className={styles.documentActionButton}
                          onClick={() => handleViewDocument(doc)}
                          title="View Document"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        </button>
                        <button
                          className={styles.documentActionButton}
                          onClick={() => handleDownloadDocument(doc)}
                          title="Download Document"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="7,10 12,15 17,10" />
                            <line x1="12" y1="15" x2="12" y2="3" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={sidePanelStyles.emptyState}>
                  <div className={sidePanelStyles.emptyIcon}>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.3">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14,2 14,8 20,8" />
                    </svg>
                  </div>
                  <div className={sidePanelStyles.emptyTitle}>No Documents</div>
                  <div className={sidePanelStyles.emptyDescription}>No documents were attached to this submission.</div>
                </div>
              )}
            </div>

            {/* Rejection Form */}
            {showRejectionForm && (isApprover || isAdmin || userRole === 'admin' || userRole === 'team_lead') &&
              (selectedSubmissionForDetails.status === 'submitted' ||
                selectedSubmissionForDetails.status === 'under_review' ||
                selectedSubmissionForDetails.status === 'resubmitted') && (
                <div className={sidePanelStyles.formGroup}>
                  <div className={sidePanelStyles.sectionHeader}>
                    <h4 className={sidePanelStyles.sectionTitleSmall}>Reject Submission</h4>
                  </div>

                  <div className={sidePanelStyles.formGroup}>
                    <label className={sidePanelStyles.formLabel}>
                      Rejection Reason <span className={sidePanelStyles.required}>*</span>
                    </label>
                    <CustomDropdown
                      options={rejectionReasonOptions}
                      value={rejectionReason}
                      onChange={(value) => setRejectionReason(value as string)}
                      placeholder="Select a reason..."
                      size="md"
                      variant="outlined"
                      className={customDropdownStyles.sizeFull}
                    />
                  </div>

                  <div className={sidePanelStyles.formGroup}>
                    <label className={sidePanelStyles.formLabel}>
                      Rejection Details <span className={sidePanelStyles.required}>*</span>
                    </label>
                    <textarea
                      className={sidePanelStyles.formControl}
                      rows={3}
                      value={rejectionDetails}
                      onChange={(e) => setRejectionDetails(e.target.value)}
                      placeholder="Provide detailed feedback for improvement..."
                      required
                    />
                  </div>

                  <div className={sidePanelStyles.formGroup}>
                    <label className={sidePanelStyles.formLabel}>
                      Review Notes
                    </label>
                    <textarea
                      className={sidePanelStyles.formControl}
                      rows={2}
                      value={reviewNotes}
                      onChange={(e) => setReviewNotes(e.target.value)}
                      placeholder="Add your review comments..."
                      required
                    />
                  </div>

                  <div className={sidePanelStyles.formActions} style={{ marginBottom: '1rem' }}>
                    <button
                      type="button"
                      className={`${buttonStyles.btn} ${buttonStyles.btnSecondary}`}
                      onClick={cancelRejection}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className={`${buttonStyles.btn} ${buttonStyles.btnDanger}`}
                      onClick={handleRejectFromSidePanel}
                      disabled={!rejectionReason || !rejectionDetails.trim()}
                    >
                      Confirm Rejection
                    </button>
                  </div>
                </div>
              )}

            {/* Quality Grade Selection - Show for pending submissions and approved submissions (for overrides) */}
            {!showRejectionForm && (isApprover || isAdmin || userRole === 'admin' || userRole === 'team_lead') &&
              (selectedSubmissionForDetails.status === 'submitted' ||
                selectedSubmissionForDetails.status === 'under_review' ||
                selectedSubmissionForDetails.status === 'resubmitted' ||
                selectedSubmissionForDetails.status === 'approved') && (
                <div className={sidePanelStyles.formGroup}>
                  <div className={sidePanelStyles.sectionHeader}>
                    <h4 className={sidePanelStyles.sectionTitleSmall}>Quality Assessment</h4>
                  </div>
                  <div className={sidePanelStyles.formGroup}>
                    <label className={sidePanelStyles.formLabel}>
                      Quality Grade <span className={sidePanelStyles.required}>*</span>
                    </label>
                    <CustomDropdown
                      options={qualityGradeOptions}
                      value={qualityGrade}
                      onChange={(value) => setQualityGrade(value as string)}
                      placeholder="Select quality grade..."
                      size="md"
                      variant="outlined"
                      className={customDropdownStyles.sizeFull}
                    />
                    <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
                      Selected grade: <strong>{qualityGrade}</strong> - {getQualityGradePercentage(qualityGrade)}% of assigned points will be awarded
                    </div>
                  </div>
                  <div className={sidePanelStyles.formGroup}>
                    <label className={sidePanelStyles.formLabel}>
                      Review Notes (Optional)
                    </label>
                    <textarea
                      className={sidePanelStyles.formControl}
                      rows={3}
                      value={reviewNotes}
                      onChange={(e) => setReviewNotes(e.target.value)}
                      placeholder="Add your review comments..."
                    />
                  </div>
                </div>
              )}

            {/* Action Buttons - Backend validates hierarchy permissions */}
            {!showRejectionForm && (isApprover || isAdmin || userRole === 'admin' || userRole === 'team_lead') &&
              (selectedSubmissionForDetails.status === 'submitted' ||
                selectedSubmissionForDetails.status === 'under_review' ||
                selectedSubmissionForDetails.status === 'resubmitted' ||
                selectedSubmissionForDetails.status === 'approved') && (
                <div className={sidePanelStyles.formActions} style={{ marginBottom: '1rem' }}>
                  <button
                    className={`${buttonStyles.btn} ${buttonStyles.btnGreenSecondary}`}
                    onClick={() => {
                      if (!qualityGrade) {
                        notificationService.error('Please select a quality grade before approving');
                        return;
                      }
                      handleApproveSubmission(selectedSubmissionForDetails);
                      closeSubmissionDetails();
                    }}
                  >
                    Approve Submission ({qualityGrade} - {getQualityGradePercentage(qualityGrade)}%)
                  </button>
                  <button
                    className={`${buttonStyles.btn} ${buttonStyles.btnDanger}`}
                    onClick={showRejectionFormInSidePanel}
                  >
                    Reject Submission
                  </button>
                </div>
              )}
          </div>
        )}
      </SidePanel>
    </div>
  );
};

export default TaskSubmission;